import { createFileRoute, Link } from "@tanstack/react-router";
import { CardMarketPanel } from "@/components/MarketPrice";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema } from "@/lib/schema";
import { CardImage } from "@/components/CardImage";
import { OFFICIAL_CARD_BY_CODE, normalizeCardCode } from "@/lib/tcg-cards";

const CODE = "EBP01-025SSP";
const URL = "https://palstars.com/cards/chillet-ssp-raw-value";
const TITLE = "Chillet Dragon Whisperer OSR/SSP Price | Pal Stars";
const DESCRIPTION =
  "Chillet OSR/SSP alternate art pricing history and raw value guide: current asking levels, completed-sale averages and how the printing tracks the Palworld TCG price index.";

const FAQ: FaqItem[] = [
  {
    q: "How much is the Chillet SSP alternate art worth raw?",
    a: "Raw, ungraded Chillet SSP copies price off the current asking board on this page. The SSP is the full-art alternate printing of EBP01-025, so it trades well above the base card and moves with chase-card demand rather than with the set as a whole.",
  },
  {
    q: "Is Chillet OSR the same card as Chillet SSP?",
    a: "Collectors use OSR loosely for the full-art alternate treatment; the printed English rarity code on the card is SSP. Both names point at the same alternate art printing of the base Chillet card, which is why this page tracks one value for it.",
  },
  {
    q: "Should I grade a raw Chillet SSP?",
    a: "Grading pays off only when the raw copy is clean enough for a high grade, because the slab premium sits mostly in Gem Mint tiers. Compare the raw asking level here against the graded board before paying submission fees.",
  },
  {
    q: "Where does Chillet SSP pricing data come from?",
    a: "Asking prices come from validated marketplace listings refreshed daily, and completed-sale averages come from TCGplayer. Lots, playsets, proxies and damaged copies are rejected before storage.",
  },
];

export const Route = createFileRoute("/cards/chillet-ssp-raw-value")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([
        ["Home", "/"],
        ["Palworld TCG Cards", "/cards"],
        ["Chillet SSP raw value", "/cards/chillet-ssp-raw-value"],
      ]),
      faqSchema(FAQ, URL),
    ],
  }),
  component: ChilletSspValuePage,
});

function ChilletSspValuePage() {
  const card = OFFICIAL_CARD_BY_CODE.get(normalizeCardCode(CODE));

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10 space-y-8">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Palworld TCG · chase card valuation</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">
          Chillet OSR/SSP Alternate Art Pricing History &amp; Value Guide
        </h1>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          Chillet's SSP alternate art ({CODE}) is the full-art parallel of the base Chillet printing from Dawn of
          Palpagos, and it is one of the printings that moves the Palworld card market most. The panel below carries the
          live asking board, the completed-sale average and the stored price history for raw, ungraded copies, updated
          daily.
        </p>
      </header>

      {card ? (
        <div className="grid gap-6 sm:grid-cols-[220px_1fr] items-start">
          <CardImage
            src={card.image}
            alt={`${CODE} Chillet SSP alternate art Palworld TCG card`}
            width={440}
            height={614}
            className="w-full rounded-xl"
          />
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              <strong className="text-foreground">Printing:</strong> {CODE} — SSP alternate art parallel of the base
              Chillet card.
            </p>
            <p>
              <strong className="text-foreground">Condition scope:</strong> raw, ungraded near-mint copies. Graded slabs
              price separately and never enter the raw average.
            </p>
            <p>
              <strong className="text-foreground">Freshness:</strong> asking quotes and completed-sale averages refresh
              daily, so the history line below extends one point per day.
            </p>
          </div>
        </div>
      ) : null}

      <CardMarketPanel code={CODE} />
      <AffiliateDisclosure compact />

      <section className="glass rounded-2xl p-6">
        <h2 className="text-xl font-bold">How Chillet SSP tracks the wider market</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          A single chase printing can run far ahead of the set it came from, so read this card against the benchmark
          before paying a premium. The{" "}
          <Link to="/tcg-price-index" className="text-primary hover:underline">
            Palworld TCG price index market trends
          </Link>{" "}
          page rebases every tracked printing to the same starting level, which shows whether Chillet is leading the
          market or just following it.
        </p>
        <ul className="mt-4 space-y-2 text-sm">
          <li>
            <Link to="/cards/$code" params={{ code: "EBP01-025" }} className="text-primary hover:underline">
              Base EBP01-025 Chillet card page
            </Link>{" "}
            — the non-parallel printing and its own price board.
          </li>
          <li>
            <Link to="/palworld-alternate-art-price-guide" className="text-primary hover:underline">
              Palworld TCG alternate art price guide
            </Link>{" "}
            — every SSP/TSP full-art printing ranked by value.
          </li>
          <li>
            <Link to="/tcg-sold-prices" className="text-primary hover:underline">
              Palworld TCG last sold prices index
            </Link>{" "}
            — completed-sale averages instead of asking levels.
          </li>
        </ul>
        <p className="mt-4 text-sm text-muted-foreground">
          If you are pricing a duplicate lot rather than one copy, total the whole stack through the unified{" "}
          <Link to="/marketplace" className="text-primary hover:underline">
            Palworld TCG bulk orders platform
          </Link>{" "}
          appraiser before you agree a trade.
        </p>
      </section>

      <FaqSection items={FAQ} heading="Chillet SSP raw value FAQ" />
    </div>
  );
}
